import { useQuery } from "react-query";
import apiService from "services/api";
import { CustomModal, CustomTable, useStudentReportPDF } from "components";
import {
  studentLessonRecordTableActions,
  studentLessonRecordTableColumns,
  parseDbDataToFormInput,
} from "./constants";
import {
  initialStudentRecordTableModalState,
  studentRecordTableModalReducer,
  studentRecordTableModalReducerActions,
} from "./reducers";
import { tabs } from "./LessonRecordTabs";
import { useEffect, useReducer, useState } from "react";
import { TabContent, Nav, NavItem, NavLink, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import { useNotificationContext } from "contexts";
import { imageTypes } from "./constants";
import { FilterableTable } from "components/FilterableTable";

const AllLessonRecordsPage = ({
  lessonRecords,
  customColumns,
  pageMode = true,
}) => {
  const { createNotification } = useNotificationContext();

  const formMethods = useForm({ reValidateMode: "onChange" });
  const { handleSubmit, reset, isDirty, getValues } = formMethods;

  const [studentRecordTableModalState, studentRecordTableModalDispatch] =
    useReducer(
      studentRecordTableModalReducer,
      initialStudentRecordTableModalState,
    );

  // const [editable, setEdi]

  const { studentRecord } = studentRecordTableModalState;
  const initialTab = tabs[0].index;
  const [tabToShow, setTabToShow] = useState(initialTab);
  const [editable, setEditable] = useState(false);
  const [refetchCheck, setRefetchCheck] = useState(false);

  const switchTab = (tabNumber) => () =>
    tabNumber !== tabToShow && setTabToShow(tabNumber);
  const [PDFView, previewPDF, exportStudentReportAsPdf, exportingPDF] =
    useStudentReportPDF({
      studentReport: studentRecordTableModalState?.studentRecord?.studentReport,
      student: studentRecordTableModalState?.studentRecord?.studentDetail,
    });

  const showModal = (row) => {
    studentRecordTableModalDispatch({
      type: studentRecordTableModalReducerActions.OPEN,
      payload: row,
    });
    const studentRecord = {
      ...row,
      inClassPhotos: row?.lessonFiles.filter(
        (lessonFile) => lessonFile.type === imageTypes.IN_CLASS_IMAGES,
      ),
      classworkPhotos: row?.lessonFiles.filter(
        (lessonFile) => lessonFile.type === imageTypes.CLASSWORK,
      ),
    };
    reset(studentRecord);
  };

  const hideModal = () => {
    setTabToShow(initialTab);
    studentRecordTableModalDispatch({
      type: studentRecordTableModalReducerActions.HIDE,
    });
  };

  const approveStudentRecord = () => {
    apiService.lessonRecord
      .approve(studentRecordTableModalState.studentRecord.id)
      .then((res) => {
        createNotification({
          type: "success",
          message: "Succesfully approve lesson report",
        });
        hideModal();
      });
  };

  const saveStudentRecordEdits = (data) => {
    apiService.lessonRecord.studentRecord
      .update(studentRecordTableModalState.studentRecord.id, {
        studentReport: data.studentReport,
        lessonFiles: [
          ...(data.inClassPhotos || []),
          ...(data.classworkPhotos || []),
        ],
      })
      .then((data) => {
        createNotification({
          type: "success",
          message: "Succesfully approve lesson report",
        });
        studentRecordTableModalDispatch({
          type: studentRecordTableModalReducerActions.OPEN,
          payload: data,
        });
        reset(parseDbDataToFormInput(data));
        setEditable(false);
        setRefetchCheck((check) => !check);
      });
  };

  const resetFields = () => reset(studentRecordTableModalState.studentRecord);

  return (
    <div className={`${pageMode && "content"} relative`}>
      <PDFView />
      <form
        onSubmit={handleSubmit(saveStudentRecordEdits)}
        id="saveStudentRecordEdits"
      />
      <form
        onSubmit={handleSubmit(approveStudentRecord)}
        id="approveStudentRecord"
      >
        <CustomModal
          show={studentRecordTableModalState.show}
          hideModal={hideModal}
          buttons={
            <>
              {studentRecordTableModalState.studentRecord
                .studentReportAdded && (
                <Button
                  onClick={previewPDF}
                  color="success"
                  disabled={exportingPDF}
                >
                  Preview Student Report
                </Button>
              )}
              {editable && (
                <>
                  <Button
                    color="success"
                    size="sm"
                    type="submit"
                    form="saveStudentRecordEdits"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      resetFields();
                      setEditable(false);
                    }}
                    color="dark"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </>
          }
        >
          <Nav pills className="nav-pills-primary mt-2">
            {tabs.map((tab) => (
              <NavItem key={tab.index} className="mb-2">
                <NavLink
                  className={`rounded px-3 py-1
                                    ${tabToShow === tab.index ? "active" : ""}
                                `}
                  onClick={switchTab(tab.index)}
                  style={{ cursor: "pointer" }}
                >
                  <small>{tab.name}</small>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={tabToShow} className="mt-4">
            {tabs.map(({ Component }, index) => (
              <Component
                key={index}
                studentRecord={studentRecord}
                {...formMethods}
                resetFields={resetFields}
                tabToShow={tabToShow}
                setEditable={setEditable}
                editable={editable}
                canEdit={true}
              />
            ))}
          </TabContent>
        </CustomModal>
      </form>
      <FilterableTable
        query={(filters) => () =>
          lessonRecords
            ? Promise.resolve(lessonRecords)
            : apiService.lessonRecord.studentRecord.get(
                undefined,
                {
                  past_records: true,
                },
                filters,
              )
        }
        action={(row) =>
          studentLessonRecordTableActions({
            showModal,
            canAdd: false,
            row,
          })
        }
        title="All Lesson Records"
        actionPrepend
        queryKey="studentRecords"
        columns={studentLessonRecordTableColumns(
          studentLessonRecordTableActions({
            showModal,
            canAdd: false,
          }),
        )}
        refetchCheck={refetchCheck}
        filters={[
          {
            filterName: "date_from",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date From",
          },
          {
            filterName: "date_to",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date To",
          },
          { filterName: "centre", operator: "contains", placeholder: "Centre" },
          {
            filterName: "teacher_name",
            operator: "contains",
            placeholder: "Teacher Name",
          },
          {
            filterName: "student_name",
            operator: "contains",
            placeholder: "Student Name",
          },
          {
            filterName: "course_name",
            operator: "contains",
            placeholder: "Subject",
          },
          {
            filterName: "lesson_name",
            operator: "contains",
            placeholder: "Lesson Topic",
          },
          {
            filterName: "is_classwork_added",
            operator: "contains",
            label: "Classwork?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "is_in_class_image_added",
            operator: "contains",
            label: "In-Class Image?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "student_report_approved",
            operator: "contains",
            label: "Approved",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "student_report_added",
            operator: "contains",
            label: "Report",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "attendance_recorded",
            operator: "contains",
            label: "Attendance",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        ]}
      />
    </div>
  );
};

export default AllLessonRecordsPage;
